<template>
    <vx-modal :padding="false"
        v-model:visible="visible"
        @submit="submit"
        size="md"
        :stacked="stacked"
        :preventClosing="preventClosing">
        <template
            v-slot:header>
            <div class="pt-6 px-6 pb-4">
                {{ model.id ? 'Update' : 'New' }} Snippet
            </div>
        </template>

        <div
            class="p-6">
            <div class="mb-3">
                <vx-label>Title</vx-label>
                <vx-input v-model="model.title"
                    type="text"
                    size="lg"
                    :error="displayFirstError(validate.title)" />
            </div>
            <div class="mb-3">
                <vx-label>Description</vx-label>
                <vx-input v-model="model.description"
                    size="lg"
                    :rows="4"
                    :error="displayFirstError(validate.description)" />
            </div>
            <div>
                <vx-label>Message</vx-label>
                <message-builder ref="messageBuilderRef"
                    v-model="model.body"
                    v-model:mediaUrl="model.media_url"
                    v-model:mediaContentType="model.media_content_type"
                    disableSnippets
                    testable
                    :disableMedia="props.disableMedia"
                    :error="displayFirstError(validate.body)"></message-builder>
            </div>
        </div>
        <template
            v-slot:footer>
            <div class="flex flex-col-reverse gap-4 justify-between lg:flex-row">

                <vx-button color="muted"
                    size="lg"
                    @click="visible = false">
                    Cancel
                </vx-button>

                <vx-button class="grow"
                    :loading="submitting"
                    @click.prevent="submit()"
                    color="primary"
                    data-test="submit-button"
                    size="lg"
                    type="button">
                    {{ model.id ? 'Update' : 'Create' }}
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { VxModal, VxLabel, VxInput, VxButton } from '@voxie/frontend-components';
import { actionsClient, snippetsClient } from '~/services';
import MessageBuilder from '~/components/general/message-builder/MessageBuilder.vue';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { computed, ref, watch } from 'vue';
import debounce from '~/utils/debounce';
import useVuelidate from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';
import { displayFirstError, validateRules } from '~/utils/validation';
import { useToasted } from '~/composables/useToasted';

const toasted = useToasted();

const teamId = Spark.state.currentTeam.id;

const visible = defineModel('visible');
const model = defineModel();

const submitting = ref(false)
const emit = defineEmits(['saved'])
const props = defineProps({
    disableMedia: {
        type: Boolean,
        default: false,
    },
    groupId: String,
    orgUnitId: Number,
})

const messageBuilderRef = ref();

const stacked = computed(() => {
    if (!messageBuilderRef.value) {
        return false;
    }
    return messageBuilderRef.value.modalOpen;
});

const preventClosing = ref(false);

watch([
    () => messageBuilderRef.value?.emojisPicker,
    () => messageBuilderRef.value?.actionsDropdown,
],
    debounce(() => {
        preventClosing.value = messageBuilderRef.value?.emojisPicker || messageBuilderRef.value?.actionsDropdown;
    }, 150)
);

watch(visible, (visible) => {
    if (!visible) {
        model.value = {
            id: null,
            title: '',
            description: '',
            body: '',
        }
        validate.value.$reset();
    }
});

const { $externalResults, catchError } = useFormRequestHandler();

const validate = useVuelidate({
    title: {
        required,
        maxLength: maxLength(120),
    },
    description: {
        maxLength: maxLength(2000),
    },
    body: {
        required,
        maxLength: maxLength(1600),
    }
}, model, {
    $externalResults,
});

const submit = async () => {
    const isValid = await validateRules(validate);
    if (submitting.value || !isValid) {
        return;
    }
    submitting.value = true;

    const context = { teamId, orgUnitId: props.orgUnitId, groupId: props.groupId };

    let request;
    if (model.value.id) {
        request = snippetsClient.updateSnippet(context, model.value.id, model.value);
    } else {
        request = snippetsClient.createSnippet(context, model.value);
    }

    try {
        const { data } = await request;
        if (!model.value.id) {
            actionsClient.send('snippet_created');
        }
        emit('saved', data);
        visible.value = false;
        toasted.success(`Snippet ${model.value.id ? 'updated' : 'created'}`);
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
}
</script>
